import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { DEFAULT_PAGE_ID, routeForPageId } from './layouts/menuData.js';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext.jsx';
import { Unauthorized, Forbidden, NotFound, ServerError } from './pages/status/StatusPage.jsx';
import { LAYOUTS } from './layouts/index.js';
import RoleRoute from './components/RoleRoute.jsx';
import { ROUTE_ROLES } from './config/routeAccess.js';
import './App.scss';

/** The bare root URL lands on the home dashboard when signed in, otherwise on sign-in. */
function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={isAuthenticated ? routeForPageId(DEFAULT_PAGE_ID) : '/sign-in'} replace />;
}

const SignInPage = lazy(() => import('./pages/user-access/SignInPage.jsx'));
const UserRegistrationPage = lazy(() => import('./pages/user-access/UserRegistrationPage.jsx'));
const ForgotPasswordPage = lazy(() => import('./pages/user-access/ForgotPasswordPage.jsx'));
const ResetPasswordPage = lazy(() => import('./pages/user-access/ResetPasswordPage.jsx'));
const UserDirectoryPage = lazy(() => import('./pages/access-management/UserDirectoryPage.jsx'));
const RolesPermissionsPage = lazy(() => import('./pages/access-management/RolesPermissionsPage.jsx'));
const PropertyLocationRegistryPage = lazy(() => import('./pages/property-location-registry/PropertyLocationRegistryPage.jsx'));
const LocationContentsSheetPage = lazy(() => import('./pages/reports-analytics/LocationContentsSheetPage.jsx'));
const RoomKitTemplatesStampingPage = lazy(() => import('./pages/property-location-registry/RoomKitTemplatesStampingPage.jsx'));
const MasterItemCataloguePage = lazy(() => import('./pages/master-catalogue-categories/MasterItemCataloguePage.jsx'));
const InwardingAssetTaggingPage = lazy(() => import('./pages/asset-inventory-custody/InwardingAssetTaggingPage.jsx'));
const TagGenerationBindingConsolePage = lazy(() => import('./pages/tag-studio-physical-onboarding/TagGenerationBindingConsolePage.jsx'));
const AssetDetailPage = lazy(() => import('./pages/asset-inventory-custody/AssetDetailPage.jsx'));
const TagResolverPage = lazy(() => import('./pages/tag-studio-physical-onboarding/TagResolverPage.jsx'));
const FieldScanHomePage = lazy(() => import('./pages/field-app/FieldScanHomePage.jsx'));
const AssetQuickViewPage = lazy(() => import('./pages/field-app/AssetQuickViewPage.jsx'));
const SyncStatusPage = lazy(() => import('./pages/field-app/SyncStatusPage.jsx'));
const FirstScanWalkBindingPage = lazy(() => import('./pages/tag-studio-physical-onboarding/FirstScanWalkBindingPage.jsx'));
const AssetCustodyMovementsPage = lazy(() => import('./pages/asset-inventory-custody/AssetCustodyMovementsPage.jsx'));
const StockPointsParManagementPage = lazy(() => import('./pages/pooled-stock-par-management/StockPointsParManagementPage.jsx'));
const LinenLaundryPage = lazy(() => import('./pages/pooled-stock-par-management/LinenLaundryPage.jsx'));
const OpeningInventoryPage = lazy(() => import('./pages/asset-inventory-custody/OpeningInventoryPage.jsx'));
const BlindCountsVarianceConsolePage = lazy(() => import('./pages/pooled-stock-par-management/BlindCountsVarianceConsolePage.jsx'));
const DepreciationRunsGlSchedulesPage = lazy(() => import('./pages/fixed-asset-accounting/DepreciationRunsGlSchedulesPage.jsx'));
const AssetDisposalsWriteOffsPage = lazy(() => import('./pages/fixed-asset-accounting/AssetDisposalsWriteOffsPage.jsx'));
const AssetValuationAnalyticsDashboardPage = lazy(() => import('./pages/reports-analytics/AssetValuationAnalyticsDashboardPage.jsx'));
const PhysicalAuditVerificationPage = lazy(() => import('./pages/audits-physical-verification/PhysicalAuditVerificationPage.jsx'));
const AuditSchedulesPage = lazy(() => import('./pages/audits-physical-verification/AuditSchedulesPage.jsx'));
const AuditReconciliationConsolePage = lazy(() => import('./pages/audits-physical-verification/AuditReconciliationConsolePage.jsx'));
const AuditExceptionQueuePage = lazy(() => import('./pages/audits-physical-verification/AuditExceptionQueuePage.jsx'));
const MaintenanceIncidentLoggingPage = lazy(() => import('./pages/maintenance-work-orders/MaintenanceIncidentLoggingPage.jsx'));
const WorkOrdersRepairManagementPage = lazy(() => import('./pages/maintenance-work-orders/WorkOrdersRepairManagementPage.jsx'));
const BanquetEventKitsBatchReconciliationPage = lazy(() => import('./pages/asset-inventory-custody/BanquetEventKitsBatchReconciliationPage.jsx'));
const GuestLoanersOverdueTrackerPage = lazy(() => import('./pages/asset-inventory-custody/GuestLoanersOverdueTrackerPage.jsx'));
const AccountSubscriptionPlansPage = lazy(() => import('./pages/account-subscription-integrations/AccountSubscriptionPlansPage.jsx'));
const IntegrationFabricConnectorsPage = lazy(() => import('./pages/account-subscription-integrations/IntegrationFabricConnectorsPage.jsx'));
const ApiWebhooksPage = lazy(() => import('./pages/account-subscription-integrations/ApiWebhooksPage.jsx'));
const PlatformConnectionsPage = lazy(() => import('./pages/account-subscription-integrations/PlatformConnectionsPage.jsx'));
const SsoCompletePage = lazy(() => import('./pages/user-access/SsoCompletePage.jsx'));
const GuestLoanersOverdueTrackerLayout = LAYOUTS['_guest-loaners-overdue-tracker-layout'];
const BanquetEventKitsBatchReconciliationLayout = LAYOUTS['_banquet-event-kits-batch-reconciliation-layout'];
const DefaultWebLayout1rckt4r = LAYOUTS['_default-web-layout-1rckt4r'];
const DefaultWebLayout7gr5z5 = LAYOUTS['_default-web-layout-7gr5z5'];
const DefaultWebLayout = LAYOUTS['_default-web-layout'];
const DefaultWebLayout1jru55b = LAYOUTS['_default-web-layout-1jru55b'];
const PropertyLocationRegistryLayout = LAYOUTS['_property-location-registry-layout'];
const AnonymousWebLayoutWvalbl = LAYOUTS['_anonymous-web-layout-wvalbl'];
const AnonymousWebLayout = LAYOUTS['_anonymous-web-layout'];

// Pages and layouts are generated per the design reference. Lazy-import them and
// wire their routes here, e.g.:
//
//   import { lazy } from 'react';
//   import { Route } from 'react-router-dom';
//   import ProtectedRoute from './components/ProtectedRoute.jsx';
//   const HomePage = lazy(() => import('./pages/HomePage.jsx'));
//
//   <Routes>
//     <Route path="/" element={<HomePage />} />
//     <Route element={<ProtectedRoute />}>
//       <Route path="/dashboard" element={<DashboardPage />} />
//     </Route>
//   </Routes>

const Loader = () => {
  const { t } = useLanguage();
  return (
    <div className="d-flex justify-content-center py-5">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">{t('Loading…')}</span>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Page routes (slug-based) go here */}
            <Route path="/" element={<RootRedirect />} />
            <Route element={<AnonymousWebLayout />}>
              <Route path="/sign-in" element={<SignInPage />} />
            </Route>
            <Route element={<AnonymousWebLayoutWvalbl />}>
              <Route path="/user-registration" element={<UserRegistrationPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/sso/complete" element={<SsoCompletePage />} />
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/user-directory']} />}>
              <Route element={<DefaultWebLayout />}>
                <Route path="/user-directory" element={<UserDirectoryPage />} />
                <Route path="/roles-permissions" element={<RolesPermissionsPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/master-item-catalogue']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/master-item-catalogue" element={<MasterItemCataloguePage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/inwarding-asset-tagging']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/inwarding-asset-tagging" element={<InwardingAssetTaggingPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/asset-detail']} />}>
              <Route element={<DefaultWebLayout />}>
                <Route path="/asset-detail" element={<AssetDetailPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/t']} />}>
              <Route element={<DefaultWebLayout />}>
                <Route path="/t/:code" element={<TagResolverPage />} />
              </Route>
            </Route>
            {/* Field app (SRS 4.2): Scan home, Asset quick view, Sync status */}
            <Route element={<RoleRoute roles={ROUTE_ROLES['/scan']} />}>
              <Route element={<DefaultWebLayout />}>
                <Route path="/scan" element={<FieldScanHomePage />} />
                <Route path="/asset-quick-view" element={<AssetQuickViewPage />} />
                <Route path="/sync-status" element={<SyncStatusPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/tag-generation-binding-console']} />}>
              <Route element={<DefaultWebLayout />}>
                <Route path="/tag-generation-binding-console" element={<TagGenerationBindingConsolePage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/stock-points-par-management']} />}>
              <Route element={<DefaultWebLayout1rckt4r />}>
                <Route path="/stock-points-par-management" element={<StockPointsParManagementPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/linen-laundry']} />}>
              <Route element={<DefaultWebLayout1rckt4r />}>
                <Route path="/linen-laundry" element={<LinenLaundryPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/opening-inventory']} />}>
              <Route element={<DefaultWebLayout1rckt4r />}>
                <Route path="/opening-inventory" element={<OpeningInventoryPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/asset-custody-movements']} />}>
              <Route element={<PropertyLocationRegistryLayout />}>
                <Route path="/asset-custody-movements" element={<AssetCustodyMovementsPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/first-scan-walk-binding']} />}>
              <Route element={<DefaultWebLayout7gr5z5 />}>
                <Route path="/first-scan-walk-binding" element={<FirstScanWalkBindingPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/blind-counts-variance-console']} />}>
              <Route element={<DefaultWebLayout7gr5z5 />}>
                <Route path="/blind-counts-variance-console" element={<BlindCountsVarianceConsolePage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/depreciation-runs-gl-schedules']} />}>
              <Route element={<PropertyLocationRegistryLayout />}>
                <Route path="/depreciation-runs-gl-schedules" element={<DepreciationRunsGlSchedulesPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/asset-disposals-write-offs']} />}>
              <Route element={<PropertyLocationRegistryLayout />}>
                <Route path="/asset-disposals-write-offs" element={<AssetDisposalsWriteOffsPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/asset-valuation-analytics-dashboard']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/asset-valuation-analytics-dashboard" element={<AssetValuationAnalyticsDashboardPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/physical-audit-verification']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/physical-audit-verification" element={<PhysicalAuditVerificationPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/audit-reconciliation-console']} />}>
              <Route element={<PropertyLocationRegistryLayout />}>
                <Route path="/audit-reconciliation-console" element={<AuditReconciliationConsolePage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/audit-exception-queue']} />}>
              <Route element={<PropertyLocationRegistryLayout />}>
                <Route path="/audit-exception-queue" element={<AuditExceptionQueuePage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/audit-schedules']} />}>
              <Route element={<PropertyLocationRegistryLayout />}>
                <Route path="/audit-schedules" element={<AuditSchedulesPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/property-location-registry']} />}>
              <Route element={<PropertyLocationRegistryLayout />}>
                <Route path="/property-location-registry" element={<PropertyLocationRegistryPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/location-contents-sheet']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/location-contents-sheet" element={<LocationContentsSheetPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/room-kit-templates-stamping']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/room-kit-templates-stamping" element={<RoomKitTemplatesStampingPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/maintenance-incident-logging']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/maintenance-incident-logging" element={<MaintenanceIncidentLoggingPage />} />
                <Route path="/work-orders-repair-management" element={<WorkOrdersRepairManagementPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/banquet-event-kits-batch-reconciliation']} />}>
              <Route element={<BanquetEventKitsBatchReconciliationLayout />}>
                <Route path="/banquet-event-kits-batch-reconciliation" element={<BanquetEventKitsBatchReconciliationPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/guest-loaners-overdue-tracker']} />}>
              <Route element={<GuestLoanersOverdueTrackerLayout />}>
                <Route path="/guest-loaners-overdue-tracker" element={<GuestLoanersOverdueTrackerPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/account-subscription-plans']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/account-subscription-plans" element={<AccountSubscriptionPlansPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/integration-fabric-connectors']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/integration-fabric-connectors" element={<IntegrationFabricConnectorsPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/developer-access']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/developer-access" element={<ApiWebhooksPage />} />
              </Route>
            </Route>
            <Route element={<RoleRoute roles={ROUTE_ROLES['/connected-platforms']} />}>
              <Route element={<DefaultWebLayout1jru55b />}>
                <Route path="/connected-platforms" element={<PlatformConnectionsPage />} />
              </Route>
            </Route>
            {/* Generic status pages; 404 is the catch-all and the fallback target for menu links to unbuilt pages */}
            <Route path="/401" element={<Unauthorized />} />
            <Route path="/403" element={<Forbidden />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="/500" element={<ServerError />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
